/**
 * GroundToggle — the ground preference control (ADR-0138 + amendment).
 *
 * Built as the TEMPORARY live-evaluation control for the ground candidates;
 * after the 2026-07-01 pick (`life-warm` won, pinned into :root) the owner
 * kept it as a permanent switcher. Every ground is a pure CSS-variable swap
 * keyed off `html[data-ground]` (see the GROUNDS blocks in index.css), so
 * cycling here restyles the whole app instantly. Persists to localStorage so
 * a choice survives reloads.
 */
import { useState } from "react";

const STORAGE_KEY = "cc-ground";

/** Order = cycle order. `life-warm` is the :root default (no attribute). */
const GROUNDS = ["life-warm", "life", "baseline", "dusk"] as const;
type Ground = (typeof GROUNDS)[number];

function isGround(v: string | null): v is Ground {
  return v !== null && (GROUNDS as readonly string[]).includes(v);
}

function apply(ground: Ground) {
  if (ground === "life-warm") {
    delete document.documentElement.dataset.ground;
  } else {
    document.documentElement.dataset.ground = ground;
  }
}

/** Called from main.tsx before first render — no flash of the default. */
export function applyStoredGround() {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (isGround(stored)) apply(stored);
}

export function GroundToggle() {
  const [ground, setGround] = useState<Ground>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isGround(stored) ? stored : "life-warm";
  });

  const cycle = () => {
    const next =
      GROUNDS[(GROUNDS.indexOf(ground) + 1) % GROUNDS.length] ?? "life-warm";
    apply(next);
    localStorage.setItem(STORAGE_KEY, next);
    setGround(next);
  };

  return (
    <button
      type="button"
      onClick={cycle}
      title="Cycle grounds (ADR-0138)"
      // bottom-LEFT: the Toaster's viewport owns bottom-right at z-[100] and
      // would cover the toggle whenever a toast is up.
      className="fixed bottom-3 left-3 z-50 rounded-chip border border-border bg-card px-2.5 py-1 font-mono text-[11px] uppercase tracking-wider text-muted-foreground shadow-sm transition-colors hover:text-foreground"
    >
      ground: {ground}
    </button>
  );
}
